import { AxiosInstance } from 'axios'
import {
  AddItemToCart,
  UpdateItemCartQuantity,
  GetCart,
  DeleteCartItem,
  GetCartResponse,
  AddItemToCartResponse,
  UpdateItemCartQuantityResponse,
  GetCheckoutSession,
  CreateCheckoutSession,
  ResumeCart,
  Cart,
  AbandonCart,
  ApplyPromoCode,
  RemovePromoCode,
} from './types/photoProductsCart'
import { cartKey, createCartStore, type CartStore } from './cartQueue'

export const CHECKOUT_SESSION_TIMEOUT_MS = 60_000

export { cartKey }

/**
 * The session's carts. There is no default client: every request names its cart
 * through `forCartType` (sc-7811).
 */
export class PhotoProductsCartClients {
  // Checkout-session requests per cart type run one at a time on the client (Hapi ADR
  // daniel-0031 dec 9). A timed-out request can still finish on the server.
  private readonly checkoutSessionQueue = new Map<string, Promise<unknown>>()
  /** Every cart read and write, one at a time per server cart, for the whole session (sc-7811). */
  readonly carts: CartStore

  constructor(private readonly api: AxiosInstance) {
    this.carts = createCartStore((cartType) => this.forCartType(cartType))
  }

  forCartType(cartType: string | undefined): PhotoProductsCartApi {
    return new PhotoProductsCartApi(this.api, cartType, this.checkoutSessionQueue)
  }
}

/** A client fixed to one cart. `undefined` is the server's default PHOTO_PRODUCTS cart. */
export class PhotoProductsCartApi {
  constructor(
    private readonly api: AxiosInstance,
    readonly cartType: string | undefined,
    private readonly checkoutSessionQueue: Map<string, Promise<unknown>>
  ) {}

  private get cartTypeParams() {
    return this.cartType ? { cartType: this.cartType } : {}
  }

  addItemToCart: AddItemToCart = async (payload) => {
    const { data } = await this.api.post<AddItemToCartResponse>('/v1/photo-products-cart/items', {
      ...payload,
      ...this.cartTypeParams,
    })

    return data
  }

  updateItemCartQuantity: UpdateItemCartQuantity = async (itemId, payload) => {
    const { data } = await this.api.put<UpdateItemCartQuantityResponse>(
      `/v1/photo-products-cart/items/${itemId}`,
      payload,
      { params: this.cartTypeParams }
    )

    return data
  }

  getCart: GetCart = async () => {
    const { data } = await this.api.get<GetCartResponse>('/v1/photo-products-cart', {
      params: this.cartTypeParams,
    })
    return data
  }

  deleteCartItem: DeleteCartItem = async (itemId) => {
    const { data } = await this.api.delete<Cart>(`/v1/photo-products-cart/items/${itemId}`, {
      params: this.cartTypeParams,
    })
    return data
  }

  createCheckoutSession: CreateCheckoutSession = () => {
    const key = cartKey(this.cartType)
    const params = this.cartTypeParams
    const previous = this.checkoutSessionQueue.get(key) ?? Promise.resolve()
    const request = previous
      .catch(() => undefined)
      .then(() =>
        this.api.post<{
          clientSecret: string
          checkoutSessionId: string
        }>('/v1/photo-products-cart/checkout-session', null, {
          params,
          // Bounds the queue: a stalled request must settle so the next can be sent.
          timeout: CHECKOUT_SESSION_TIMEOUT_MS,
        })
      )
      .then(({ data }) => data)
    this.checkoutSessionQueue.set(key, request)
    return request
  }

  getCheckoutSession: GetCheckoutSession = async (id: string) => {
    const { data } = await this.api.get<{
      amount: number
      email: string
    }>(`/v1/photo-products-cart/checkout-session/${id}`, {
      params: this.cartTypeParams,
    })
    return data
  }

  applyPromoCode: ApplyPromoCode = async (code) => {
    // cartType travels in the body here, as for addItemToCart (Hapi validator).
    const { data } = await this.api.post<Cart>('/v1/photo-products-cart/promo-code', {
      code,
      ...this.cartTypeParams,
    })
    return data
  }

  removePromoCode: RemovePromoCode = async () => {
    const { data } = await this.api.delete<Cart>('/v1/photo-products-cart/promo-code', {
      params: this.cartTypeParams,
    })
    return data
  }

  resumeCart: ResumeCart = async () => {
    const { data } = await this.api.patch<Cart>('/v1/photo-products-cart/resume', null, {
      params: this.cartTypeParams,
    })

    return data
  }

  abandonCart: AbandonCart = async () => {
    const { data } = await this.api.patch<Cart>('/v1/photo-products-cart/abandon', null, {
      params: this.cartTypeParams,
    })

    return data
  }
}
